import moment from 'moment';

export interface ICancellationRule {
  daysToEvent: number;
  rate: number;
  toleranceRate?: number | null;
}

export interface ICancellationRuleSelector extends ICancellationRule {
  minCapacity?: number | null;
  maxCapacity?: number | null;
  minOvernight?: number | null;
  maxOvernight?: number | null;
  minTotalGuests?: number | null;
  maxTotalGuests?: number | null;
}

export interface IHotelProps {
  businessCountry: string;
  maxCapacityRooms?: number | null;
  maxCapacityPeople?: number | null;
}

export interface IHotelWithContractsProps extends IHotelProps {
  contracts: IHotelContractProps[];
}

export interface IHotelContractProps {
  type: string;
  validFrom: Date;
  validTo: Date;
}

export interface ICompanyProps {
  isPremium: boolean;
}

export interface IOfferProps {
  days: IOfferDayProps[];
}

export interface IOfferDayProps {
  overnightRoomsDZ: number;
  overnightRoomsEZ: number;
  totalGuests: number;
}

export const hasHotelActiveContract = (hotel: IHotelWithContractsProps, type: string, offerDate: Date) => {
  const offerDateDay = moment(offerDate).startOf('day');
  return hotel.contracts.some(
    c => c.type === type && offerDateDay >= moment(c.validFrom).startOf('day') && offerDateDay <= moment(c.validTo).endOf('day'),
  );
};

export const hasHotelActivePartnerContract = (hotel: IHotelWithContractsProps, offerDate?: Date) => {
  const checkDate = offerDate || new Date();
  const isPartner =
    hasHotelActiveContract(hotel, 'PARTNER_HOTEL_CONTRACT', checkDate) ||
    hasHotelActiveContract(hotel, 'MEETING_PACKAGE_IMPORT', checkDate) ||
    hasHotelActiveContract(hotel, 'SYMPOSION_HOTEL_CONTRACT', checkDate) ||
    hasHotelActiveContract(hotel, 'FRIEND_HOTEL_CONTRACT', checkDate);
  return isPartner;
};

/*
Kundenstornobedingungen Österreich
Premium: kleiner 7 Kalendertage vor Veranstaltungsbeginn (um 00:00) 100% der vereinbarten Leistung Toleranz 10%
Premium: kleiner 21 Kalendertage vor Veranstaltungsbeginn (um 00:00) 50% der vereinbarten Leistung Toleranz 10%

Kundenstornobedingungen Deutschland
Premium: kleiner 7 Kalendertage über 50 % der Zimmerkapazität oder über 30 Teilnehmer gebucht → 100% der vereinbarten Leistung, Toleranz 10%
Premium: kleiner 7 Kalendertage 100% der vereinbarten Leistung Toleranz 10%
Premium: kleiner 28 Kalendertage über 50 % der Zimmerkapazität oder über 30 Teilnehmer gebucht → 50% der vereinbarten Leistung, Toleranz 10%
Premium: kleiner 28 Kalendertage 50% der vereinbarten Leistung Toleranz 10%
*/

export const listPremiumClientCancellationRules = (businessCountry: string): ICancellationRule[] => {
  if (businessCountry === 'AT') {
    return [
      {
        daysToEvent: 7,
        rate: 1,
        toleranceRate: 0.1,
      },
      {
        daysToEvent: 21,
        rate: 0.5,
        toleranceRate: 0.1,
      },
    ];
  } else {
    return [
      {
        daysToEvent: 7,
        rate: 1,
        toleranceRate: 0.1,
      },
      {
        daysToEvent: 28,
        rate: 0.5,
        toleranceRate: 0.1,
      },
    ];
  }
};

// Kundenstornobedingungen Österreich & Deutschland
// Vermittlung: kleiner 21 Kalendertage und über 50 % der Zimmerkapazität oder über 50 Teilnehmer gebucht → 100% der vereinbarten Leistung, Toleranz 0%
// Vermittlung: kleiner 21 Kalendertage 100% der vereinbarten Leistung Toleranz 0%
// Vermittlung: kleiner 42 Kalendertage und über 50 % der Zimmerkapazität oder über 50 Teilnehmer gebucht → 50% der vereinbarten Leistung Toleranz 0%
// Vermittlung: kleiner 42 Kalendertage 50% der vereinbarten Leistung Toleranz 0%
export const listDefaultClientCancellationRules = (businessCountry: string): ICancellationRule[] => {
  return [
    {
      daysToEvent: 21,
      rate: 1,
    },
    {
      daysToEvent: 42,
      rate: 0.5,
    },
  ];
};

export const getHotelCancellationRules = (
  isPartnerHotel: boolean,
  businessCountry: string,
  isPremiumClient: boolean,
  hotelRules: ICancellationRule[],
): ICancellationRule[] => {
  if (isPremiumClient) {
    if (isPartnerHotel) {
      return listPremiumClientCancellationRules(businessCountry);
    } else {
      if (hotelRules.length > 0) return hotelRules;
      return listPremiumClientCancellationRules(businessCountry);
    }
  } else {
    if (hotelRules.length > 0) return hotelRules;
    return listDefaultClientCancellationRules(businessCountry);
  }
};

export const triggerPremiumPartnerEscape = (hotel: IHotelProps, offer: IOfferProps) => {
  const maxOvernightRooms = offer.days.reduce((acc, day) => Math.max(acc, day.overnightRoomsDZ + day.overnightRoomsEZ), 0);
  const maxDailyGuests = offer.days.reduce((acc, day) => Math.max(acc, day.totalGuests), 0);

  if (maxDailyGuests > 30 || (hotel.maxCapacityRooms && maxOvernightRooms > hotel.maxCapacityRooms * 0.5)) {
    return true;
  }
};

export const canChangeOfferCancellationRules = (offerStatus: string, versionStatus: string) => {
  if (offerStatus === 'PENDING') {
    if (versionStatus === 'WAITFORHOTELRESERVATION') return false;
    return true;
  }
  if (offerStatus === 'APPROVED') {
    if (versionStatus === 'APPROVED') return false;
    return true;
  }
  return false;
};

export const canChangeCancellationRules = (hotel: IHotelWithContractsProps, company: ICompanyProps, offer: IOfferProps, offerDate?: Date) => {
  if (company.isPremium && hasHotelActivePartnerContract(hotel, offerDate)) {
    if (triggerPremiumPartnerEscape(hotel, offer)) {
      return true;
    } else {
      return false;
    }
  }
  return true;
};

export const isCancellationRulesValid = (rules: ICancellationRule[]) => {
  // Check that each rule has valid rate and daysToEvent
  const basicValidation = rules.every(rule => {
    return rule.daysToEvent > 0 && rule.rate >= 0 && rule.rate <= 1;
  });

  if (!basicValidation) return false;

  // Check that all daysToEvent values are different
  const daysToEventValues = rules.map(rule => rule.daysToEvent);
  const uniqueDaysToEvent = new Set(daysToEventValues);

  return uniqueDaysToEvent.size === rules.length;
};

export const isCancellationRulesValidWithCategories = (rules: (ICancellationRule & Partial<ICancellationRuleSelector>)[]): boolean => {
  if (!rules.length) return true;

  const hasOvernight = rules.some(r => r.minOvernight != null || r.maxOvernight != null);
  const hasSeminar = rules.some(r => r.minTotalGuests != null || r.maxTotalGuests != null);

  if (hasOvernight || hasSeminar) {
    // Two-category mode: validate within each category only
    const overnightRules = rules
      .filter(r => r.minOvernight != null || r.maxOvernight != null)
      .map(r => ({ daysToEvent: r.daysToEvent, rate: r.rate }));
    const seminarRules = rules
      .filter(r => r.minTotalGuests != null || r.maxTotalGuests != null)
      .map(r => ({ daysToEvent: r.daysToEvent, rate: r.rate }));
    return (
      (overnightRules.length === 0 || isCancellationRulesValid(overnightRules)) &&
      (seminarRules.length === 0 || isCancellationRulesValid(seminarRules))
    );
  }

  // Single category / general: validate globally
  return isCancellationRulesValid(rules.map(r => ({ daysToEvent: r.daysToEvent, rate: r.rate })));
};

export const hasCancellationRuleChanges = (
  rules1: (ICancellationRule & Partial<ICancellationRuleSelector>)[],
  rules2: (ICancellationRule & Partial<ICancellationRuleSelector>)[],
) => {
  if (rules1.length !== rules2.length) return true;

  const isEqual = (a: number | null | undefined, b: number | null | undefined) => {
    if (a === b) return true;
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    return a === b;
  };

  const sortedRules1 = [...rules1].sort((a, b) => a.daysToEvent - b.daysToEvent);
  const sortedRules2 = [...rules2].sort((a, b) => a.daysToEvent - b.daysToEvent);

  return sortedRules1.some((rule, index) => {
    const other = sortedRules2[index];
    return (
      rule.daysToEvent !== other.daysToEvent ||
      rule.rate !== other.rate ||
      !isEqual(rule.minOvernight, other.minOvernight) ||
      !isEqual(rule.maxOvernight, other.maxOvernight) ||
      !isEqual(rule.minTotalGuests, other.minTotalGuests) ||
      !isEqual(rule.maxTotalGuests, other.maxTotalGuests)
    );
  });
};
